import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CalendarClock, Package, Pencil, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AssetFormDialog } from "@/components/assets/AssetFormDialog";
import { AssetGallery } from "@/components/assets/AssetGallery";
import { EmptyState } from "@/components/common/EmptyState";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { AssetLinks } from "@/components/assets/AssetLinks";
import { TaskRow, useMaintenance } from "@/components/household/MaintenancePanel";
import { useHouseholdDetail } from "@/hooks/use-household-detail";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useHousehold } from "@/context/HouseholdContext";
import { CATEGORY_LABELS } from "@/hooks/use-assets";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

const Row = ({ label, value }) => value ? <div className="flex justify-between gap-4 py-2.5 text-sm"><span className="text-muted-foreground">{label}</span><span className="text-right font-medium">{value}</span></div> : null;

const WarrantyBadge = ({ end, days }) => {
  if (!end) return <span className="text-xs text-muted-foreground" data-testid="asset-warranty-status">Aucune garantie renseignée</span>;
  const left = days >= 60 ? `${Math.floor(days / 30.44)} mois restants` : `${days} j restants`;
  const [txt, tone] = days < 0 ? ["Garantie expirée", "tone-rose"] : days <= 60 ? [`Expire dans ${days} j`, "tone-butter"] : [`Sous garantie · ${left}`, "tone-mint"];
  return <span className={cn("tone-chip inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold", tone)} data-testid="asset-warranty-status"><ShieldCheck className="h-3 w-3" />{txt}</span>;
};

const AssetTasks = ({ aid }) => {
  const qc = useQueryClient();
  const { canEdit } = useHouseholdDetail();
  const tasks = (useMaintenance().data || []).filter((t) => t.asset_id === aid);
  return (
    <div className="card-surface px-5 py-4" data-testid="asset-maintenance">
      <div className="flex items-center gap-2 text-sm font-semibold"><CalendarClock className="h-4 w-4" />Entretiens</div>
      {tasks.length ? <ul className="divide-y divide-border">{tasks.map((t) => <TaskRow key={t.id} t={t} canEdit={canEdit} onDone={() => ["maintenance", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }))} />)}</ul>
        : <p className="pt-2 text-sm text-muted-foreground">Aucun entretien lié. Planifiez-en un depuis Mon Foyer › Entretiens.</p>}
    </div>
  );
};

export default function AssetDetail() {
  const { aid } = useParams();
  const { active } = useHousehold();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { money, longDate } = useFormat();
  const { canEdit } = useHouseholdDetail();
  const [edit, setEdit] = useState(false);
  const { data: a, isLoading, isError, refetch } = useQuery({
    queryKey: ["asset", active?.id, aid], queryFn: () => api.get(`/households/${active.id}/assets/${aid}`).then((r) => r.data), enabled: !!active, retry: false,
  });
  if (isError) return <EmptyState icon={Package} title="Équipement introuvable" action={<Button variant="outline" onClick={() => navigate("/foyer")}>Retour</Button>} />;
  if (isLoading || !a) return <Skeleton className="h-64 rounded-2xl" />;

  const remove = async () => {
    try {
      await api.delete(`/households/${active.id}/assets/${a.id}`);
      qc.invalidateQueries({ queryKey: ["assets", active.id] });
      toast.success("Équipement supprimé");
      navigate("/foyer");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="asset-detail-page">
      <Link to="/foyer" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="asset-back-link"><ArrowLeft className="h-4 w-4" />Mon Foyer</Link>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <AssetGallery a={a} canEdit={canEdit} />
        <div className="space-y-5">
          <div>
            <div className="eyebrow">{a.room || "Sans pièce"} · {CATEGORY_LABELS[a.category]}</div>
            <h1 className="mt-1 text-3xl font-extrabold" data-testid="asset-detail-name">{a.name}</h1>
            <div className="mt-2"><WarrantyBadge end={a.warranty_end} days={a.warranty_days_left} /></div>
          </div>
          {a.current_value != null && (
            <div className="card-surface tone-wash tone-mint flex flex-wrap items-end gap-4 px-5 py-4" data-testid="asset-value-card">
              <div className="flex-1"><div className="text-xs text-muted-foreground">Valeur actuelle {a.value_source === "manual" ? "(saisie)" : "estimée"}</div>
                <div className="num text-3xl font-extrabold" data-testid="asset-current-value">{money(a.current_value, { maximumFractionDigits: 0 })}</div></div>
              <div className="text-right text-xs text-muted-foreground">{a.purchase_price != null && <>Acheté {money(a.purchase_price, { maximumFractionDigits: 0 })}<br /></>}
                {a.value_source === "manual" ? a.auto_value != null && `Calcul auto : ${money(a.auto_value, { maximumFractionDigits: 0 })}` : `Décote ${a.rate_effective} %/an`}</div>
            </div>
          )}
          <div className="card-surface divide-y divide-border px-5 py-2">
            <Row label="Marque" value={a.brand} /><Row label="Modèle" value={a.model} /><Row label="N° de série" value={a.serial_number} />
            <Row label="Date d'achat" value={a.purchase_date && longDate(new Date(a.purchase_date))} />
            <Row label="Prix d'achat" value={a.purchase_price != null && money(a.purchase_price, { maximumFractionDigits: 2 })} />
            <Row label="Fin de garantie" value={a.warranty_end && longDate(new Date(a.warranty_end))} />
            <Row label="Notes" value={a.notes} />
          </div>
          <div className="flex gap-2">
            <Button className="rounded-full" onClick={() => setEdit(true)} data-testid="asset-edit-button"><Pencil className="mr-2 h-4 w-4" />Modifier</Button>
            <ConfirmDelete title="Supprimer cet équipement ?" text={`« ${a.name} » sera retiré de l'inventaire du foyer.`} onConfirm={remove}>
              <Button variant="ghost" className="rounded-full text-destructive" data-testid="asset-delete-button"><Trash2 className="mr-2 h-4 w-4" />Supprimer</Button>
            </ConfirmDelete>
          </div>
          <AssetTasks aid={a.id} />
          <AssetLinks a={a} />
        </div>
      </div>
      {edit && <AssetFormDialog open asset={a} onOpenChange={setEdit} onSaved={() => refetch()} />}
    </div>
  );
}
